import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  useChangePassword,
  useLogout,
  useUpdateProfile,
  useUser,
  useUserCredits,
} from "../hooks/useAuth";
import { PartyTopBar } from "../party/PartyChrome";
import "../party/partyShell.css";
import type { PartyShellPalette } from "../theme/theme";
import { MixpanelEvents, trackEvent } from "../utils/mixpanel";

const UpdateProfilePage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const partyPal = (theme.palette as unknown as { party: PartyShellPalette }).party;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: creditsData } = useUserCredits();
  const { data: userData, isLoading: isLoadingUser } = useUser();
  const updateProfile = useUpdateProfile();
  const changePassword = useChangePassword();
  const logout = useLogout();

  const [tab, setTab] = useState(() => {
    const n = parseInt(searchParams.get("tab") || "0", 10);
    return n === 1 ? 1 : 0;
  });
  const [deleteAccountModalOpen, setDeleteAccountModalOpen] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    username: "",
  });
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [passwordErrors, setPasswordErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const n = parseInt(searchParams.get("tab") || "0", 10);
    setTab(n === 1 ? 1 : 0);
  }, [searchParams]);

  useEffect(() => {
    trackEvent(MixpanelEvents.PAGE_VIEWED, {
      page_name: "Update Profile Page",
      path: "/update-profile",
      tab,
    });
  }, [tab]);

  useEffect(() => {
    if (userData) {
      setFormData({
        first_name: userData.first_name || "",
        last_name: userData.last_name || "",
        username: userData.username || "",
      });
    }
  }, [userData]);

  const handleTabChange = (_: React.SyntheticEvent, v: number) => {
    setTab(v);
    setPasswordChangeSuccess(false);
    if (v === 0) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tab: "1" }, { replace: true });
    }
  };

  const fieldPaperSx = {
    p: 2.5,
    mb: 2,
    borderRadius: 3,
    bgcolor: partyPal.paper,
    color: partyPal.paperInk,
    border: `1.5px solid color-mix(in oklab, ${partyPal.paperInk} 12%, transparent)`,
    boxShadow: "var(--shadow-card, 0 12px 40px rgba(0,0,0,0.12))",
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "14px",
      bgcolor: "rgba(0,0,0,0.04)",
      fontFamily: "var(--font-body, inherit)",
      "& fieldset": {
        borderColor: `color-mix(in oklab, ${partyPal.paperInk} 20%, transparent)`,
      },
      "&:hover fieldset": {
        borderColor: `color-mix(in oklab, ${partyPal.paperInk} 35%, transparent)`,
      },
    },
    "& .MuiInputLabel-root": {
      color: `color-mix(in oklab, ${partyPal.paperInk} 68%, transparent)`,
    },
    "& .MuiOutlinedInput-input": {
      color: partyPal.paperInk,
    },
  };

  const handleSaveProfile = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.username.trim()) {
      newErrors.username = `${t("auth.username")} — ${t("common.error")}`;
    }

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    updateProfile.mutate(formData, {
      onSuccess: () => {
        navigate("/");
      },
    });
  };

  const handleChangePassword = async () => {
    const newErrors: Record<string, string> = {};

    if (!passwordData.currentPassword) {
      newErrors.currentPassword = t("auth.passwordRequired");
    }

    if (!passwordData.newPassword) {
      newErrors.newPassword = t("auth.passwordRequired");
    } else if (passwordData.newPassword.length < 8) {
      newErrors.newPassword = t("auth.passwordMinLength");
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      newErrors.confirmPassword = t("auth.passwordsDoNotMatch");
    }

    setPasswordErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    changePassword.mutate(
      {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
        password_confirm: passwordData.confirmPassword,
      },
      {
        onSuccess: () => {
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setPasswordErrors({});
          setPasswordChangeSuccess(true);
        },
      }
    );
  };

  const handleLogout = () => {
    logout.mutate();
  };

  const handleConfirmDeleteAccount = () => {
    trackEvent(MixpanelEvents.ACCOUNT_DELETED, {
      user_id: userData?.id,
      username: userData?.username,
    });
    setDeleteAccountModalOpen(false);
    logout.mutate();
  };

  if (isLoadingUser) {
    return (
      <Box
        className="party-shell-root"
        dir={i18n.language === "ar" ? "rtl" : "ltr"}
        lang={i18n.language || "en"}
        data-party-theme="midnight"
        data-party-type="rubik"
        sx={{
          minHeight: "100dvh",
          width: "100%",
          bgcolor: partyPal.bg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress sx={{ color: partyPal.hot }} />
      </Box>
    );
  }

  if (!userData) {
    navigate("/login");
    return null;
  }

  return (
    <Box
      className="party-shell-root"
      dir={i18n.language === "ar" ? "rtl" : "ltr"}
      lang={i18n.language || "en"}
      data-party-theme="midnight"
      data-party-type="rubik"
      sx={{
        minHeight: "100dvh",
        width: "100%",
        bgcolor: partyPal.bg,
        color: partyPal.ink,
      }}
    >
      <Box className="party-app-frame" sx={{ maxWidth: 520, mx: "auto", pb: 6 }}>
        <PartyTopBar title={t("profile.title")} onBack={() => navigate("/")} />

        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            px: 1.5,
            mb: 2,
            minHeight: 48,
            "& .MuiTab-root": {
              color: partyPal.inkDim,
              fontWeight: 800,
              textTransform: "none",
              fontFamily: "var(--font-display, inherit)",
              fontSize: 15,
            },
            "& .MuiTab-root.Mui-selected": {
              color: partyPal.ink,
            },
            "& .MuiTabs-indicator": {
              bgcolor: partyPal.hot,
              height: 3,
              borderRadius: 1,
            },
          }}
        >
          <Tab label={t("profile.editProfile")} disableRipple />
          <Tab label={t("auth.changePassword")} disableRipple />
        </Tabs>

        <Box component="main" sx={{ px: 2.5 }}>
          {tab === 0 && (
            <Stack spacing={0}>
              <Paper elevation={0} className="party-kan-card" sx={{ ...fieldPaperSx, ["--card-bg" as string]: partyPal.paper } as React.CSSProperties}>
                <Typography
                  className="party-display"
                  sx={{ fontSize: 22, mb: 2, color: partyPal.paperInk }}
                >
                  {t("profile.profile")}
                </Typography>

                {creditsData != null && (
                  <Alert
                    severity="info"
                    sx={{
                      mb: 2,
                      borderRadius: 2,
                      bgcolor: `color-mix(in oklab, ${partyPal.paperInk} 6%, ${partyPal.paper})`,
                      color: partyPal.paperInk,
                      "& .MuiAlert-icon": { color: partyPal.violet },
                    }}
                  >
                    {t("profile.availableCredits")}: <strong>{creditsData.credit}</strong>
                  </Alert>
                )}

                {updateProfile.error && (
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {(updateProfile.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
                      t("common.error")}
                  </Alert>
                )}

                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label={t("auth.firstName")}
                    value={formData.first_name}
                    onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                    sx={textFieldSx}
                  />
                  <TextField
                    fullWidth
                    label={t("auth.lastName")}
                    value={formData.last_name}
                    onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                    sx={textFieldSx}
                  />
                  <TextField
                    fullWidth
                    required
                    label={t("auth.username")}
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    error={!!errors.username}
                    helperText={errors.username}
                    sx={textFieldSx}
                  />
                  <Button
                    type="button"
                    variant="contained"
                    className="party-btn party-btn-hot"
                    fullWidth
                    onClick={handleSaveProfile}
                    disabled={updateProfile.isPending}
                    sx={{ mt: 0.5, py: 1.5, textTransform: "none", fontWeight: 800 }}
                  >
                    {updateProfile.isPending ? <CircularProgress size={24} color="inherit" /> : t("common.save")}
                  </Button>
                </Stack>
              </Paper>

              <Paper elevation={0} className="party-kan-card" sx={{ ...fieldPaperSx, ["--card-bg" as string]: partyPal.paper } as React.CSSProperties}>
                <Typography className="party-display" sx={{ fontSize: 20, mb: 2, color: partyPal.paperInk }}>
                  {t("profile.accountActions")}
                </Typography>
                <Stack spacing={1.5}>
                  <Button
                    type="button"
                    variant="outlined"
                    fullWidth
                    onClick={handleLogout}
                    disabled={logout.isPending}
                    className="party-btn party-btn-paper"
                    sx={{
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 800,
                      borderColor: `color-mix(in oklab, ${partyPal.paperInk} 22%, transparent)`,
                      color: partyPal.paperInk,
                    }}
                  >
                    {logout.isPending ? (
                      <CircularProgress size={22} sx={{ color: partyPal.paperInk }} />
                    ) : (
                      t("auth.logout")
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outlined"
                    fullWidth
                    onClick={() => setDeleteAccountModalOpen(true)}
                    sx={{
                      py: 1.5,
                      textTransform: "none",
                      fontWeight: 800,
                      borderColor: `color-mix(in oklab, ${partyPal.danger} 55%, transparent)`,
                      color: partyPal.danger,
                    }}
                  >
                    {t("profile.deleteAccount")}
                  </Button>
                </Stack>
              </Paper>
            </Stack>
          )}

          {tab === 1 && (
            <Paper elevation={0} className="party-kan-card" sx={{ ...fieldPaperSx, ["--card-bg" as string]: partyPal.paper } as React.CSSProperties}>
              <Typography className="party-display" sx={{ fontSize: 22, mb: 1, color: partyPal.paperInk }}>
                {t("auth.changePassword")}
              </Typography>
              <Typography sx={{ fontSize: 13, color: `color-mix(in oklab, ${partyPal.paperInk} 55%, transparent)`, mb: 2, lineHeight: 1.5 }}>
                {t("profile.passwordTabHint")}
              </Typography>

              {passwordChangeSuccess && (
                <Alert
                  severity="success"
                  onClose={() => setPasswordChangeSuccess(false)}
                  sx={{ mb: 2, borderRadius: 2, color: partyPal.paperInk }}
                >
                  {t("profile.passwordChangeSuccess")}
                </Alert>
              )}

              {changePassword.error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  {(changePassword.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
                    t("common.error")}
                </Alert>
              )}

              <Stack spacing={2}>
                <TextField
                  fullWidth
                  type="password"
                  autoComplete="current-password"
                  label={t("auth.currentPassword")}
                  value={passwordData.currentPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, currentPassword: e.target.value })
                  }
                  error={!!passwordErrors.currentPassword}
                  helperText={passwordErrors.currentPassword}
                  sx={textFieldSx}
                />
                <TextField
                  fullWidth
                  type="password"
                  autoComplete="new-password"
                  label={t("auth.password")}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  error={!!passwordErrors.newPassword}
                  helperText={passwordErrors.newPassword}
                  sx={textFieldSx}
                />
                <TextField
                  fullWidth
                  type="password"
                  autoComplete="new-password"
                  label={t("auth.confirmPassword")}
                  value={passwordData.confirmPassword}
                  onChange={(e) =>
                    setPasswordData({ ...passwordData, confirmPassword: e.target.value })
                  }
                  error={!!passwordErrors.confirmPassword}
                  helperText={passwordErrors.confirmPassword}
                  sx={textFieldSx}
                />
                <Button
                  type="button"
                  variant="contained"
                  className="party-btn party-btn-hot"
                  fullWidth
                  onClick={handleChangePassword}
                  disabled={
                    !passwordData.currentPassword ||
                    !passwordData.newPassword ||
                    passwordData.newPassword !== passwordData.confirmPassword ||
                    changePassword.isPending
                  }
                  sx={{ mt: 0.5, py: 1.5, textTransform: "none", fontWeight: 800 }}
                >
                  {changePassword.isPending ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t("common.save")
                  )}
                </Button>
              </Stack>
            </Paper>
          )}
        </Box>
      </Box>

      <Dialog
        open={deleteAccountModalOpen}
        onClose={() => setDeleteAccountModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: partyPal.bg2,
            color: partyPal.ink,
            borderRadius: 3,
            border: `1.5px solid ${partyPal.inkFaint}`,
            backgroundImage: "none",
          },
        }}
      >
        <DialogTitle sx={{ fontFamily: "var(--font-display)", fontWeight: 800, color: partyPal.ink }}>
          {t("profile.deleteAccount")}
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: partyPal.inkDim, lineHeight: 1.55, fontSize: 15 }}>
            {t("profile.deleteAccountConfirm")}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            type="button"
            onClick={() => setDeleteAccountModalOpen(false)}
            sx={{ textTransform: "none", color: partyPal.inkDim, fontWeight: 700 }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            onClick={handleConfirmDeleteAccount}
            variant="contained"
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: partyPal.danger,
              "&:hover": { bgcolor: `color-mix(in oklab, ${partyPal.danger} 88%, #000)` },
            }}
          >
            {t("profile.deleteAccount")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UpdateProfilePage;
